import type { UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query'
import type { MaybeRef } from 'vue'
import type { Alert, AlertEnhanced } from '@/types/alerts/alert.model'
import type { Organisation } from '@/types/organisation.model'
import type { MutationOptions, QueryOptions } from '@/types/query-options.model'
import type { Site } from '@/types/site.model'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { unref } from 'vue'
import { useAlerts } from '@/composables/useAlerts'
import { AlertLevel } from '@/types/alerts/alert-level.enum'
import {
  CACHE_KEY_ALERT_HISTORY,
  CACHE_KEY_CONNECTION_ID,
  CACHE_KEY_CURRENT,
  CACHE_KEY_ODYSSEY_URL,
  CACHE_KEY_OPEN_ALERTS,
  CACHE_KEY_ORGANISATIONS,
  CACHE_KEY_PARTNER_ACCESS,
  CACHE_KEY_SITES,
} from '@/utils/constants/cache'
import { deleteRequest, getRequest, postRequest, putRequest } from './core/api-client'

// GET /organisations
async function getOrganisations(signal?: AbortSignal | undefined): Promise<Organisation[]> {
  const response = await getRequest<Organisation[]>('/organisations', { signal })

  if (response && response.length > 1) {
    return response.sort((a, z) => (a.name as string).localeCompare(z.name as string))
  }

  return response
}

export function getOrganisationsQuery(useQueryOptions?: QueryOptions<Organisation[]>): UseQueryReturnType<Organisation[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS],
    queryFn: async ({ signal }) => await getOrganisations(signal),
    ...useQueryOptions,
  })
}

// GET /organisations/:id
export async function getOrganisation(id: string, signal?: AbortSignal | undefined): Promise<Organisation> {
  return await getRequest<Organisation>(`/organisations/${id}`, { signal })
}

export function getOrganisationQuery(id: MaybeRef<string>, useQueryOptions?: QueryOptions<Organisation>): UseQueryReturnType<Organisation, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, id],
    queryFn: async ({ signal }) => await getOrganisation(unref(id), signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...useQueryOptions,
  })
}

// GET /organisations/current/open-alerts
async function getOrganisationOpenAlerts(signal?: AbortSignal | undefined): Promise<Alert[]> {
  return await getRequest<Alert[]>(`/organisations/current/open-alerts`, { signal })
}

const { getAlertDefinitionContent, getAlertSeverityLabel, getAlertStatusLabel } = useAlerts()

export function getOrganisationOpenAlertsQuery(useQueryOptions?: QueryOptions<AlertEnhanced[]>): UseQueryReturnType<AlertEnhanced[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, CACHE_KEY_CURRENT, CACHE_KEY_OPEN_ALERTS],
    queryFn: async ({ signal }) => {
      const response = await getOrganisationOpenAlerts(signal)

      const _alerts
        = response?.map((alert) => {
          return {
            ...alert,
            definition: getAlertDefinitionContent(alert.alertDefinitionId),
            severityLabel: getAlertSeverityLabel(alert.severity),
            statusLabel: getAlertStatusLabel(alert.status),
          }
        }) ?? undefined

      return _alerts ?? response
    },
    ...useQueryOptions,
  })
}

// GET /organisations/current/alerts
async function getOrganisationAlerts(startDateTime: string, endDateTime: string, severities: string[], statuses: string[], siteIds: string[]): Promise<Alert[]> {
  return await postRequest('/organisations/current/alerts', {
    startDateTime,
    endDateTime,
    severities,
    statuses,
    siteIds,
    levels: [
      AlertLevel.Organisation,
      AlertLevel.Site,
      AlertLevel.AssetFamily,
      AlertLevel.Asset,
    ],
  })
}

export function getOrganisationAlertsQuery(startDateTime: MaybeRef<string>, endDateTime: MaybeRef<string>, severities: MaybeRef<string[]>, statuses: MaybeRef<string[]>, siteIds: MaybeRef<string[]>, useQueryOptions?: QueryOptions<Alert[]>): UseQueryReturnType<Alert[], Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, CACHE_KEY_CURRENT, CACHE_KEY_ALERT_HISTORY, [startDateTime, endDateTime], severities, statuses, siteIds],
    queryFn: async () =>
      await getOrganisationAlerts(unref(startDateTime), unref(endDateTime), unref(severities), unref(statuses), unref(siteIds)),
    ...useQueryOptions,
  })
}

// GET /organisations/current/partner-access
async function getOrganisationPartnerAccess(signal?: AbortSignal | undefined): Promise<boolean> {
  return await getRequest<boolean>(`/organisations/current/partner-access`, { signal })
}

export function getOrganisationPartnerAccessQuery(id: string, useQueryOptions?: QueryOptions<boolean>): UseQueryReturnType<boolean, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, CACHE_KEY_CURRENT, CACHE_KEY_PARTNER_ACCESS],
    queryFn: async ({ signal }) => await getOrganisationPartnerAccess(signal),
    ...useQueryOptions,
  })
}

// GET /organisations/:id/sites
async function getOrganisationSites(id: string, signal?: AbortSignal | undefined): Promise<Site[]> {
  return getRequest(`/organisations/${id}/sites`, { signal })
}

export function getOrganisationSitesQuery(id: MaybeRef<string>, useQueryOptions?: QueryOptions<Site[]>): UseQueryReturnType<Site[], Site[]> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, id, CACHE_KEY_SITES],
    queryFn: async ({ signal }) => await getOrganisationSites(unref(id), signal),
    ...useQueryOptions,
  })
}

// POST /organisations/:id
async function createOrganisation(organisation: Organisation): Promise<Organisation> {
  return await postRequest<Organisation, Organisation>(`/organisations`, organisation)
}

export function createOrganisationMutation(useMutationOptions?: MutationOptions<Organisation>): UseMutationReturnType<Organisation, Error, Organisation, Error> {
  return useMutation({
    mutationFn: async (organisation: Organisation) => await createOrganisation(organisation),
    ...useMutationOptions,
  })
}

// POST /organisations/current/partner-access
async function grantOrganisationPartnerAccess(signal?: AbortSignal | undefined): Promise<void> {
  return await postRequest(`/organisations/current/partner-access`, { signal })
}

export function grantOrganisationPartnerAccessMutation(useMutationOptions?: MutationOptions<void>): UseMutationReturnType<void, Error, void, Error> {
  return useMutation({
    mutationFn: async ({ signal }) => await grantOrganisationPartnerAccess(signal),
    ...useMutationOptions,
  })
}

// PUT /organisations/:id
async function updateOrganisation(organisation: Organisation): Promise<Organisation> {
  return await putRequest<Organisation, Organisation>(`/organisations/${organisation.id}`, organisation)
}

export function updateOrganisationMutation(useMutationOptions?: MutationOptions<Organisation>): UseMutationReturnType<Organisation, Error, Organisation, Error> {
  return useMutation({
    mutationFn: async (organisation: Organisation) => await updateOrganisation(organisation),
    ...useMutationOptions,
  })
}

// DELETE /organisations/:id
async function archiveOrganisation(id: string): Promise<boolean> {
  return await deleteRequest(`/organisations/${id}`)
}

export function archiveOrganisationMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async (id: string) => await archiveOrganisation(id),
    ...useMutationOptions,
  })
}

interface MergeOrganisationsRequest {
  destinationOrganisationId: string
  sourceOrganisationId: string
}

// POST /organisations/merge
async function mergeOrganisations(mergeRequest: MergeOrganisationsRequest): Promise<boolean> {
  return await postRequest<MergeOrganisationsRequest, boolean>(`/organisations/merge`, mergeRequest)
}

export function mergeOrganisationsMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, MergeOrganisationsRequest, Error> {
  return useMutation({
    mutationFn: async (mergeRequest: MergeOrganisationsRequest) => await mergeOrganisations(mergeRequest),
    ...useMutationOptions,
  })
}

// DELETE /organisations/current/partner-access
async function revokeOrganisationPartnerAccess(signal?: AbortSignal | undefined): Promise<boolean> {
  return await deleteRequest(`/organisations/current/partner-access`, { signal })
}

export function revokeOrganisationPartnerAccessMutation(useMutationOptions?: MutationOptions<boolean>): UseMutationReturnType<boolean, Error, string, Error> {
  return useMutation({
    mutationFn: async ({ signal }) => await revokeOrganisationPartnerAccess(signal),
    ...useMutationOptions,
  })
}

// GET /organisations/current/connection/id
async function getOrganisationConnectionId(signal?: AbortSignal | undefined): Promise<string> {
  return await getRequest<string>(`/organisations/current/connection/id`, { signal })
}

export function getOrganisationConnectionIdQuery(useQueryOptions?: QueryOptions<string>): UseQueryReturnType<string, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, CACHE_KEY_CURRENT, CACHE_KEY_CONNECTION_ID],
    queryFn: async ({ signal }) => await getOrganisationConnectionId(signal),
    ...useQueryOptions,
  })
}

// GET /organisations/current/connection/secret
export async function getOrganisationConnectionSecret(signal?: AbortSignal | undefined): Promise<string> {
  return await getRequest<string>(`/organisations/current/connection/secret`, { signal })
}

// GET /organisations/current/integration/odyssey-url
async function getOrganisationOdysseyUrl(signal?: AbortSignal | undefined): Promise<string> {
  return await getRequest<string>(`/organisations/current/integration/odyssey-url`, { signal })
}

export function getOrganisationOdysseyUrlQuery(useQueryOptions?: QueryOptions<string>): UseQueryReturnType<string, Error> {
  return useQuery({
    queryKey: [CACHE_KEY_ORGANISATIONS, CACHE_KEY_CURRENT, CACHE_KEY_ODYSSEY_URL],
    queryFn: async ({ signal }) => await getOrganisationOdysseyUrl(signal),
    ...useQueryOptions,
  })
}

// GET /organisations/current/integration/odyssey-api-key
export async function getOrganisationOdysseyApiKey(signal?: AbortSignal | undefined): Promise<string> {
  return await getRequest<string>(`/organisations/current/integration/odyssey-api-key`, { signal })
}
